/**
 * EventAction - CTA button for actionable events
 *
 * Provides clear, action-oriented buttons answering "What should I do next?"
 */

import React from 'react';
import {
  ArrowRight,
  Calendar,
  Download,
  FileText,
  Plus,
  Upload,
  ExternalLink,
} from 'lucide-react';
import './EventAction.css';

export interface EventActionProps {
  /** Action label */
  label: string;

  /** Action type determines styling */
  type?: 'primary' | 'secondary' | 'link';

  /** Icon to display */
  icon?: 'arrow' | 'calendar' | 'download' | 'file' | 'plus' | 'upload' | 'external';

  /** Whether the action is disabled */
  disabled?: boolean;

  /** Click handler */
  onClick?: () => void;

  /** Optional href for link type */
  href?: string;

  /** Additional CSS class */
  className?: string;
}

const ICONS: Record<NonNullable<EventActionProps['icon']>, React.FC<{ className?: string }>> = {
  arrow: ArrowRight,
  calendar: Calendar,
  download: Download,
  file: FileText,
  plus: Plus,
  upload: Upload,
  external: ExternalLink,
};

export const EventAction: React.FC<EventActionProps> = ({
  label,
  type = 'primary',
  icon,
  disabled = false,
  onClick,
  href,
  className = '',
}) => {
  const Icon = icon ? ICONS[icon] : null;

  const content = (
    <>
      {Icon && <Icon className="event-action__icon" />}
      <span className="event-action__label">{label}</span>
    </>
  );

  const baseClassName = `event-action event-action--${type} ${className}`;

  if (type === 'link' && href) {
    return (
      <a
        href={href}
        className={baseClassName}
        onClick={onClick}
        target="_blank"
        rel="noopener noreferrer"
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type="button"
      className={baseClassName}
      disabled={disabled}
      onClick={onClick}
    >
      {content}
    </button>
  );
};

export default EventAction;
